import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useConnections } from '@/queries/connections/useConnections'
import { useCreateLiveDataSource } from '@/queries/data-sources/useCreateLiveDataSource'
import { useUploadDataSource } from '@/queries/data-sources/useUploadDataSource'
import { useStytchOrganization } from '@stytch/react/b2b'
import { Link, Upload } from 'lucide-react'
import { useRef, useState } from 'react'

interface Props {
	open: boolean
	onClose: () => void
}

export const CreateDataSourceDialog = ({ open, onClose }: Props) => {
	const [activeTab, setActiveTab] = useState('csv')
	const { organization } = useStytchOrganization()
	const [file, setFile] = useState<File | null>(null)
	const [dataSourceName, setDataSourceName] = useState('')
	const [selectedConnectionId, setSelectedConnectionId] = useState<string>('')
	const inputRef = useRef<HTMLInputElement | null>(null)

	const { data: connections } = useConnections(organization?.organization_id)
	const { mutateAsync: uploadCsv, isPending: isUploadingCsv } = useUploadDataSource()
	const { mutateAsync: createLiveDataSource, isPending: isCreatingLive } = useCreateLiveDataSource()

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.[0]) {
			const selectedFile = e.target.files[0]
			setFile(selectedFile)
			// Auto-fill name from filename (without extension)
			if (!dataSourceName) {
				const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '')
				setDataSourceName(nameWithoutExt)
			}
		}
	}

	const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
		e.preventDefault()
		if (e.dataTransfer.files?.[0]) {
			const droppedFile = e.dataTransfer.files[0]
			setFile(droppedFile)
			if (!dataSourceName) {
				const nameWithoutExt = droppedFile.name.replace(/\.[^/.]+$/, '')
				setDataSourceName(nameWithoutExt)
			}
		}
	}

	const handleUploadCsv = async () => {
		if (!file || !dataSourceName.trim() || !organization?.organization_id) return
		try {
			await uploadCsv({
				workspaceId: organization?.organization_id,
				file,
				name: dataSourceName.trim(),
			})
			handleClose()
		} catch (err) {
			console.error(err)
			// TODO: toast error
		}
	}

	const handleCreateLiveDataSource = async () => {
		if (!selectedConnectionId || !dataSourceName.trim() || !organization?.organization_id) return
		try {
			await createLiveDataSource({
				workspaceId: organization.organization_id,
				name: dataSourceName.trim(),
				connectionId: selectedConnectionId,
			})
			handleClose()
		} catch (err) {
			console.error(err)
			// TODO: toast error
		}
	}

	const handleClose = () => {
		setFile(null)
		setDataSourceName('')
		setSelectedConnectionId('')
		setActiveTab('csv')
		onClose()
	}

	const canUploadCsv = file && dataSourceName.trim()
	const canCreateLive = selectedConnectionId && dataSourceName.trim()
	const isProcessing = isUploadingCsv || isCreatingLive

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Create Data Source</DialogTitle>
					<DialogDescription>
						Add custom data to your workspace by uploading a CSV file or connecting to a live data
						source.
					</DialogDescription>
				</DialogHeader>

				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger value="csv" className="flex items-center gap-2">
							<Upload className="h-4 w-4" />
							Upload CSV
						</TabsTrigger>
						<TabsTrigger value="connection" className="flex items-center gap-2">
							<Link className="h-4 w-4" />
							Live Connection
						</TabsTrigger>
					</TabsList>

					{/* CSV Upload Tab */}
					<TabsContent value="csv" className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="csv-name">Data Source Name</Label>
							<Input
								id="csv-name"
								placeholder="Enter a name for this data source"
								value={dataSourceName}
								onChange={(e) => setDataSourceName(e.target.value)}
							/>
						</div>

						{/* File Dropzone */}
						<div className="flex items-center justify-center w-full">
							<label
								htmlFor="dropzone-file"
								className="flex flex-col items-center justify-center w-full h-48 cursor-pointer rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
								onDrop={handleDrop}
								onDragOver={(e) => e.preventDefault()}
							>
								<div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
									{file ? (
										<>
											<div className="mb-2 p-2 rounded-full bg-green-100 dark:bg-green-900">
												<Upload className="h-6 w-6 text-green-600 dark:text-green-400" />
											</div>
											<p className="text-sm font-medium text-gray-600 dark:text-gray-400">
												{file.name}
											</p>
											<p className="text-xs text-gray-500 dark:text-gray-400">
												{(file.size / 1024 / 1024).toFixed(2)} MB
											</p>
										</>
									) : (
										<>
											<div className="mb-4 p-3 rounded-full bg-gray-100 dark:bg-gray-600">
												<Upload className="h-8 w-8 text-gray-500 dark:text-gray-400" />
											</div>
											<p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
												<span className="font-semibold">Click to upload</span> or drag and drop
											</p>
											<p className="text-xs text-gray-500 dark:text-gray-400">
												CSV file up to 50 MB
											</p>
										</>
									)}
								</div>
								<input
									id="dropzone-file"
									type="file"
									accept=".csv"
									className="hidden"
									ref={inputRef}
									onChange={handleFileChange}
								/>
							</label>
						</div>

						<div className="flex justify-end gap-2 pt-4">
							<Button variant="outline" onClick={handleClose} disabled={isProcessing}>
								Cancel
							</Button>
							<Button
								onClick={handleUploadCsv}
								disabled={!canUploadCsv || isProcessing}
								className="flex items-center gap-2"
							>
								{isUploadingCsv && (
									<div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
								)}
								Upload & Process
							</Button>
						</div>
					</TabsContent>

					{/* Live Connection Tab */}
					<TabsContent value="connection" className="space-y-4">
						<div className="space-y-2">
							<Label htmlFor="live-name">Data Source Name</Label>
							<Input
								id="live-name"
								placeholder="Enter a name for this data source"
								value={dataSourceName}
								onChange={(e) => setDataSourceName(e.target.value)}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="connection-select">Select Connection</Label>
							<Select value={selectedConnectionId} onValueChange={setSelectedConnectionId}>
								<SelectTrigger>
									<SelectValue placeholder="Choose a connection to sync data from" />
								</SelectTrigger>
								<SelectContent>
									{connections?.map((connection) => (
										<SelectItem key={connection.id} value={connection.id.toString()}>
											<div className="flex items-center gap-2">
												<div
													className={`h-2 w-2 rounded-full ${
														connection.status === 'active'
															? 'bg-green-500'
															: connection.status === 'error'
																? 'bg-red-500'
																: 'bg-yellow-500'
													}`}
												/>
												<span>{connection.name}</span>
												<span className="text-xs text-muted-foreground">
													({connection.platform})
												</span>
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{connections?.length === 0 && (
								<p className="text-sm text-muted-foreground">
									No connections available. Create a connection first in Settings.
								</p>
							)}
						</div>

						<div className="rounded-lg bg-blue-50 dark:bg-blue-950 p-3">
							<div className="flex items-start gap-2">
								<Link className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
								<div className="text-sm">
									<p className="font-medium text-blue-800 dark:text-blue-200">Live Data Source</p>
									<p className="text-blue-700 dark:text-blue-300">
										This will sync fresh data daily from your selected connection and automatically
										update your grids.
									</p>
								</div>
							</div>
						</div>

						<div className="flex justify-end gap-2 pt-4">
							<Button variant="outline" onClick={handleClose} disabled={isProcessing}>
								Cancel
							</Button>
							<Button
								onClick={handleCreateLiveDataSource}
								disabled={!canCreateLive || isProcessing}
								className="flex items-center gap-2"
							>
								{isCreatingLive && (
									<div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
								)}
								Create Data Source
							</Button>
						</div>
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	)
}
